export function useSeo() {
  const config = useRuntimeConfig()
  const route = useRoute()
  const { locale } = useI18n()

  const baseUrl = computed(() => config.public.frontUrl || 'https://www.uianet.org')

  /**
   * Generate comprehensive SEO metadata for pages
   * @param {Object} options - SEO configuration options
   * @param {string} options.title - Page title
   * @param {string} options.description - Page description
   * @param {string} options.type - Open Graph type (default: 'website')
   * @param {string|Object} options.image - Image URL or object with url property
   * @param {string} options.siteName - Site name (default: 'Union Internationale des Avocats')
   * @param {Array} options.items - Array of items for structured data (for listing pages)
   * @param {string} options.schemaType - Schema.org type (default: 'WebPage')
   * @param {boolean} options.includeHreflang - Include hreflang tags (default: true)
   * @param {string} options.basePath - Base path for hreflang generation
   * @returns {Object} useHead configuration object
   */
  function generateSeoMeta({
    title,
    description,
    type = 'website',
    image,
    siteName = 'Union Internationale des Avocats',
    items = null,
    schemaType = 'WebPage',
    includeHreflang = true,
    basePath = null
  }) {
    const finalDescription = description || ''
    const finalTitle = title || 'Union Internationale des Avocats'

    // Image handling
    const getImageUrl = () => {
      if (!image) return `${baseUrl.value}${config.public.logo}`
      if (typeof image === 'string') return image.startsWith('http') ? image : `${baseUrl.value}${image}`
      if (image.url) return image.url.startsWith('http') ? image.url : `${baseUrl.value}${image.url}`
      return `${baseUrl.value}${config.public.logo}`
    }

    // Build links array
    const links = [
      {
        rel: 'canonical',
        href: `${baseUrl.value}${route.path}`
      }
    ]

    // Add hreflang tags if requested
    if (includeHreflang && basePath !== null) {
      const hreflangLinks = [
        {
          rel: 'alternate',
          hreflang: 'fr',
          href: `${baseUrl.value}/fr${basePath}`
        },
        {
          rel: 'alternate',
          hreflang: 'en',
          href: `${baseUrl.value}/en${basePath}`
        },
        {
          rel: 'alternate',
          hreflang: 'es',
          href: `${baseUrl.value}/es${basePath}`
        },
        {
          rel: 'alternate',
          hreflang: 'x-default',
          href: `${baseUrl.value}/fr${basePath}`
        }
      ]
      links.push(...hreflangLinks)
    }

    // Build meta tags
    const metaTags = [
      {
        name: 'robots',
        content: 'index, follow'
      },
      {
        name: 'title',
        content: finalTitle
      },
      {
        name: 'description',
        content: finalDescription
      },
      // Open Graph tags
      {
        property: 'og:title',
        content: finalTitle
      },
      {
        property: 'og:description',
        content: finalDescription
      },
      {
        property: 'og:type',
        content: type
      },
      {
        property: 'og:image',
        content: getImageUrl()
      },
      {
        property: 'og:site_name',
        content: siteName
      },
      {
        property: 'og:url',
        content: `${baseUrl.value}${route.fullPath}`
      }
    ]

    // Build structured data
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': schemaType,
      name: finalTitle,
      description: finalDescription,
      url: `${baseUrl.value}${route.fullPath}`,
      publisher: {
        '@type': 'Organization',
        name: siteName,
        url: baseUrl.value,
        logo: {
          '@type': 'ImageObject',
          url: `${baseUrl.value}${config.public.logo}`
        }
      }
    }

    // Add items for listing pages
    if (items && Array.isArray(items) && schemaType === 'CollectionPage') {
      structuredData.mainEntity = {
        '@type': 'ItemList',
        numberOfItems: items.length,
        itemListElement: items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          item: {
            '@type': 'Article',
            headline: item.title,
            description: item.description,
            url: `${baseUrl.value}/articles/${item.documentId}`,
            datePublished: item.createdAt,
            dateModified: item.updatedAt,
            author: {
              '@type': 'Organization',
              name: siteName
            },
            publisher: {
              '@type': 'Organization',
              name: siteName
            },
            ...(item.thumbnail && {
              image: {
                '@type': 'ImageObject',
                url: `${baseUrl.value}${item.thumbnail.url}`
              }
            })
          }
        }))
      }
    }

    return {
      title: finalTitle,
      htmlAttrs: {
        lang: locale.value
      },
      link: links,
      meta: metaTags,
      script: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(structuredData)
        }
      ]
    }
  }

  return {
    generateSeoMeta,
    baseUrl
  }
}
